import { toast } from 'react-toastify';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { addDays, format, subDays } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import pt from 'date-fns/locale/pt';

import { updateCurrentDate } from 'store/modules/usuario/actions';
import api from 'services/api';
import config from 'config/environment';
import { downloadFile } from 'utils';

import Button from 'components/Button';
import SelectDate from 'components/SelectDate';
import { Container, ColumnContent, Grafico as Card } from './styles';
import { FiDownload } from 'react-icons/fi';
import AppError from 'utils/AppError';

interface IReponseRecebidoFaltante {
  emissora: string;
  materiaisFaltantes: number;
  materiaisAtrasados: number;
  materiaisRecebidos: number;
}
interface IResponseSemMapa {
  emissora: string;
  insercoesSemMapa: number;
  materiaisSemMapa: number;
}
interface IResponseSlide {
  emissora: string;
  insercoesSlide: number;
  partidosSlide: number;
}
interface IDataSets {
  label: string;
  data: number[];
  backgroundColor: string;
}
interface IGrafico {
  labels: string[];
  datasets: IDataSets[];
}

export default function DashboardEleicoes() {
  const options = {
    scales: {
      yAxes: [
        {
          ticks: {
            suggestedMin: 0,
            suggestedMax: 10,
            stepSize: 2,
          },
        },
      ],
    },
  };
  const [grafico1, setGrafico1] = useState<IGrafico>({} as IGrafico);
  const [grafico2, setGrafico2] = useState<IGrafico>({} as IGrafico);
  const [grafico3, setGrafico3] = useState<IGrafico>({} as IGrafico);
  const [loadingDownload, setLoadingDownload] = useState(false);

  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { currentDate } = useSelector((state: any) => state.usuario);

  /**
   * USE MEMO
   */
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    }
  }, [currentDate]);

  /**
   * USE CALLBACK
   */
  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  function normalizeGrafico1(response: IReponseRecebidoFaltante[]) {
    const grafico: IGrafico = {
      labels: response.map((g: IReponseRecebidoFaltante) => g.emissora),
      datasets: [
        {
          label: 'FALTANTES',
          data: response.map(
            (g: IReponseRecebidoFaltante) => g.materiaisFaltantes,
          ),
          backgroundColor: '#FF6384',
        },
        {
          label: 'ATRASADOS',
          data: response.map(
            (g: IReponseRecebidoFaltante) => g.materiaisAtrasados,
          ),
          backgroundColor: '#FFCE56',
        },
        {
          label: 'RECEBIDOS',
          data: response.map(
            (g: IReponseRecebidoFaltante) => g.materiaisRecebidos,
          ),
          backgroundColor: '#57e2ad',
        },
      ],
    };
    setGrafico1(grafico);
  }

  function normalizaGrafico2(response: IResponseSlide[]) {
    const grafico: IGrafico = {
      labels: response.map((g: IResponseSlide) => g.emissora),
      datasets: [
        {
          label: 'INSERÇÕES',
          data: response.map((g: IResponseSlide) => g.insercoesSlide),
          backgroundColor: '#FF6384',
        },
        {
          label: 'PARTIDOS',
          data: response.map((g: IResponseSlide) => g.partidosSlide),
          backgroundColor: '#FFCE56',
        },
      ],
    };
    setGrafico2(grafico);
  }

  function normalizaGrafico3(response: IResponseSemMapa[]) {
    const grafico: IGrafico = {
      labels: response.map((g: IResponseSemMapa) => g.emissora),
      datasets: [
        {
          label: 'MATERIAIS',
          data: response.map((g: IResponseSemMapa) => g.materiaisSemMapa),
          backgroundColor: '#FF6384',
        },
        {
          label: 'INSERÇÕES',
          data: response.map((g: IResponseSemMapa) => g.insercoesSemMapa),
          backgroundColor: '#FFCE56',
        },
      ],
    };
    setGrafico3(grafico);
  }

  const handleDownloadRelatorio = useCallback(async (isBloco: boolean) => {
    setLoadingDownload(true);
    try {
      await downloadFile({
        url: `${config.API_URL}/eleicoes/insercao/${
          isBloco ? 'relatoriobloco' : 'relatorio'
        }`,
        name: isBloco ? 'Relatório de Bloco' : 'Relatório de Inserções',
        fromAPI: true,
      });
    } catch (error) {
      toast.error('Falha ao baixar o relatório. Tente novamente mais tarde');
    } finally {
      setLoadingDownload(false);
    }
  }, []);

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadDashboard() {
      try {
        const response = await api.get('/eleicoes/dashboard', {
          params: {
            data: format(new Date(currentDate), 'yyyy-M-d').toString(),
          },
        });
        if (
          response &&
          response.data &&
          response.data.graficoRecebidoFaltante &&
          response.data.graficoSemMapa &&
          response.data.graficoSlide
        ) {
          normalizeGrafico1(response.data.graficoRecebidoFaltante);
          normalizaGrafico3(response.data.graficoSemMapa);
          normalizaGrafico2(response.data.graficoSlide);
        }
      } catch (e) {
        setGrafico1({
          labels: [],
          datasets: [],
        });
        setGrafico2({
          labels: [],
          datasets: [],
        });
        setGrafico3({
          labels: [],
          datasets: [],
        });
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
    }
    loadDashboard();
  }, [currentDate]);

  return (
    <Container>
      <header>
        <nav>
          <Button
            color="primary"
            onClick={() => handleDownloadRelatorio(false)}
          >
            {loadingDownload ? (
              'Gerando relatório...'
            ) : (
              <>
                <FiDownload /> Relatório Inserção
              </>
            )}
          </Button>
          {dateFormatted && currentDate && (
            <SelectDate
              dateFormatted={dateFormatted}
              currentDateZoned={String(new Date(currentDate))}
              handleNextDay={handleNextDay}
              handleDateChange={handleDateChange}
              handlePrevDay={handlePrevDay}
            />
          )}
          <Button color="primary" onClick={() => handleDownloadRelatorio(true)}>
            {loadingDownload ? (
              'Gerando relatório...'
            ) : (
              <>
                <FiDownload /> Relatório Bloco
              </>
            )}
          </Button>
        </nav>
      </header>
      <ColumnContent>
        <Card>
          <p>Gráfico de Recebidos/Atrasados/Faltantes</p>
          <Bar data={grafico1} height={80} options={options} />
        </Card>
        <Card>
          <p>Gráfico de Inserções Slide/Partido</p>
          <Bar data={grafico2} height={80} options={options} />
        </Card>
        <Card>
          <p>Gráfico de Materiais/Inseções Sem Mapa</p>
          <Bar data={grafico3} height={80} options={options} />
        </Card>
      </ColumnContent>
    </Container>
  );
}
